import { useEffect, useRef } from "react"
import {
  ArrowSmallUpIcon,
  ArrowSmallDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import classNames from "classnames"

const GROUP_LABEL_PREFIX = "Grouped by"

function GroupControl({ label, desc, onGroup }) {
  return (
    <>
      <span>{label}</span>
      <span
        className="mx-1.5 cursor-pointer rounded-md p-1 hover:bg-primary-9 hover:text-neutral-2"
        onClick={(e) => {
          e.stopPropagation()
          onGroup()
        }}
      >
        {desc ? (
          <ArrowSmallDownIcon className="h-3.5 w-3.5" />
        ) : (
          <ArrowSmallUpIcon className="h-3.5 w-3.5" />
        )}
      </span>
    </>
  )
}

function GroupingControl({
  groupColumn,
  groupColumnLabel,
  groupDesc,
  secondGroupColumn,
  secondGroupColumnLabel,
  secondGroupDesc,
  onGroup,
}) {
  const hasSecondGroupBy = !!secondGroupColumn

  return (
    <>
      <span className="mr-1">{GROUP_LABEL_PREFIX}</span>
      <GroupControl
        label={groupColumnLabel}
        desc={groupDesc}
        onGroup={() => onGroup(groupColumn)}
      />
      {hasSecondGroupBy && (
        <GroupControl
          label={secondGroupColumnLabel}
          desc={secondGroupDesc}
          onGroup={() => onGroup(secondGroupColumn)}
        />
      )}
    </>
  )
}

function GroupingDescription({ groupColumnLabel, secondGroupColumnLabel }) {
  var result = `${GROUP_LABEL_PREFIX} ${groupColumnLabel}`

  if (secondGroupColumnLabel) {
    result += `, ${secondGroupColumnLabel}`
  }

  return <span>{result}</span>
}

export default function GroupByIndicator({
  groupColumnLabel,
  groupColumn,
  groupDesc,
  secondGroupColumnLabel,
  secondGroupColumn,
  secondGroupDesc,
  onGroup,
  showDetails,
  columnCount,
  hasFrozenColumns,
  headerHeight,
}) {
  const groupIndicatorRef = useRef()
  const canChangeGroupOrder = showDetails

  useEffect(() => {
    const currentGroupIndicatorRef = groupIndicatorRef.current
    if (currentGroupIndicatorRef) {
      currentGroupIndicatorRef.style.top = `${headerHeight}px`
    }
  }, [headerHeight])

  const removeGrouping = (e) => {
    e.stopPropagation()
    onGroup(null)
  }

  const groupingContainerClasses = classNames(
    "flex items-center p-2 overflow-visible",
    {
      "sticky left-0 h-11": hasFrozenColumns,
    }
  )

  return (
    <div
      style={{ gridColumn: `1 / span ${columnCount}` }}
      ref={groupIndicatorRef}
      className="sticky left-0 z-20 flex items-center space-x-2 border-primary-5 bg-primary-4 text-sm text-white shadow-md"
      data-testid="datatable-group-by-indicator"
    >
      <div className={groupingContainerClasses}>
        <span
          className="mr-1.5 cursor-pointer rounded-md p-1 hover:bg-primary-9 hover:text-neutral-2"
          onClick={removeGrouping}
        >
          <XMarkIcon className="h-5 w-5" />
        </span>
        {canChangeGroupOrder ? (
          <GroupingControl
            groupColumn={groupColumn}
            groupColumnLabel={groupColumnLabel}
            groupDesc={groupDesc}
            secondGroupColumn={secondGroupColumn}
            secondGroupColumnLabel={secondGroupColumnLabel}
            secondGroupDesc={secondGroupDesc}
            onGroup={onGroup}
          />
        ) : (
          <GroupingDescription
            groupColumnLabel={groupColumnLabel}
            secondGroupColumnLabel={secondGroupColumnLabel}
          />
        )}
      </div>
    </div>
  )
}
